// Admin section inline guides.
// Each entry is keyed by "admin-{tab-value}" and contains 2–5 steps that
// walk the admin through the section, its filters, and any destructive or
// irreversible actions to be aware of.
// Targets use data-tour="admin-{tab}-section" added to each TabsContent
// wrapper in admin.tsx, plus more specific data-tour attrs on key sub-elements.

import type { TourConfig } from "@/components/tour/app-guide-registry";

export const adminLeadsGuide: TourConfig = {
  id: "admin-leads",
  steps: [
    {
      target: '[data-tour="admin-leads-section"]',
      title: "Leads",
      body: "Every visitor who fills out the PadPulse interest form lands here. Each row shows their name, email, property count, city, timeline, and the date they reached out.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-leads-table"]',
      title: "Lead table",
      body: "Click a row to expand it and view the full note they submitted. You can also toggle the 'Contacted' flag to mark a lead as reached so you don't reach out twice.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-leads-export"]',
      title: "Export CSV",
      body: "Download all leads as a CSV at any time using the Export button in the summary bar above. Handy for loading into a CRM or sharing with the sales team.",
      placement: "bottom",
    },
  ],
};

export const adminConferenceLeadsGuide: TourConfig = {
  id: "admin-conference-leads",
  steps: [
    {
      target: '[data-tour="admin-conference-leads-section"]',
      title: "Conference Leads",
      body: "Leads captured through the Co-Living Conference landing page flow are collected here, separate from regular site leads. These contacts expressed interest specifically around a conference event.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-conference-leads-section"]',
      title: "Conference-specific data",
      body: "Conference leads include the session or event they registered for, so follow-ups can be tailored to what they attended. Treat these as warm leads with higher intent than general site visitors.",
      placement: "top",
    },
  ],
};

export const adminFeaturesGuide: TourConfig = {
  id: "admin-features",
  steps: [
    {
      target: '[data-tour="admin-features-section"]',
      title: "Feature Requests",
      body: "Every feature request submitted through the in-app feedback form appears here. They are sorted by most votes so you can see what the user base wants most.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-features-table"]',
      title: "Request details",
      body: "Click any row to read the full description, see who submitted it, and view the current status (open, in review, shipped, declined). Update the status to communicate progress back to users.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-features-table"]',
      title: "Status updates",
      body: "Changing a status to 'shipped' or 'declined' does NOT automatically notify the requester — that must be done manually if needed. Update status primarily for your own prioritization tracking.",
      placement: "top",
    },
  ],
};

export const adminFounderIntentsGuide: TourConfig = {
  id: "admin-founder-intents",
  steps: [
    {
      target: '[data-tour="admin-founder-intents-section"]',
      title: "Founder Signups",
      body: "Everyone who reserved a Founder Circle spot is listed here. Each row shows their tier (Starter/Pro/Elite), payment status, and whether their PadPulse account has been activated yet.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-founder-intents-grant-access"]',
      title: "Activation workflow",
      body: "Click 'Grant Access' to create the host's PadPulse account and send the welcome email. A confirmation dialog will show you the tier, free room count, and price before you commit.",
      placement: "left",
    },
    {
      target: '[data-tour="admin-founder-intents-grant-access"]',
      title: "⚠️ Irreversible — verify before activating",
      body: "Granting access sends an email immediately and creates the account with the comped subscription. You cannot un-activate — only manually change their subscription status afterward. Always confirm the correct tier and email before clicking Grant Access.",
      placement: "left",
    },
    {
      target: '[data-tour="admin-founder-intents-table"]',
      title: "Payment tracking",
      body: "Use the Paid / Unpaid toggle to record Mercury-wired payments. Mercury webhook events auto-mark intents paid when they arrive — the toggle is for manual corrections only.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-founder-intents-filters"]',
      title: "Filter and sort",
      body: "Filter by tier or paid status, and toggle sort direction to find the next unactivated founder in line. The default sort is registration order (ascending) so earlier signups are handled first.",
      placement: "bottom",
    },
  ],
};

export const adminAppUsersGuide: TourConfig = {
  id: "admin-app-users",
  steps: [
    {
      target: '[data-tour="admin-app-users-section"]',
      title: "App Users",
      body: "Every host who has created a PadPulse account is listed here. The table shows their plan, subscription status, property count, referral code, and last login.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-app-users-table"]',
      title: "User actions",
      body: "Click the ⋯ menu on any row to access per-user actions: Override Subscription Status, Impersonate, Suspend, Reinstate, or view the Subscription History audit trail.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-app-users-impersonate"]',
      title: "⚠️ Impersonation — use with care",
      body: "Click Impersonate on any row to log into that user's account (you'll confirm your password first). Every action you take while impersonating is attributed to them in the system, but recorded in the Impersonation Log. Always end the session as soon as you're done. Avoid making changes on their behalf unless explicitly authorized.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-app-users-suspend"]',
      title: "⚠️ Suspension — immediate effect",
      body: "Suspending a user signs them out of every active session instantly and blocks new logins. They receive an email with your reason. Use Reinstate to restore access. Always provide a clear reason — it appears in their notification email.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-app-users-sub-status"]',
      title: "Subscription status override",
      body: "Use 'comped' to grant free paid-tier access (e.g., manual onboarding or a support concession). Every override requires a reason that is stored in the audit trail and visible in the Subscription History for that user.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-app-users-filters"]',
      title: "Filters and search",
      body: "Filter by plan, subscription status, or referral code. Search by name, email, or phone. Use the 'Has Crisp chat' indicator to spot users with waiting support conversations.",
      placement: "bottom",
    },
  ],
};

export const adminImpersonationLogGuide: TourConfig = {
  id: "admin-impersonation-log",
  steps: [
    {
      target: '[data-tour="admin-impersonation-log-heading"]',
      title: "Impersonation Log",
      body: "Every admin impersonation session is recorded here — who initiated it, which user or vendor was impersonated, when it started and ended, and what actions were taken during the session.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-impersonation-log-search"]',
      title: "Why this exists",
      body: "The impersonation log is your accountability trail. If a host reports an unexpected change or a support issue, this log lets you identify which admin session caused it and what happened. Search by impersonator email, target email, or date range.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-impersonation-log-table"]',
      title: "Two log types",
      body: "The panel shows two combined log types: host (app user) impersonation sessions and vendor impersonation sessions. Vendor entries include the vendor name and the team member who initiated the session.",
      placement: "top",
    },
  ],
};

export const adminVendor2faGuide: TourConfig = {
  id: "admin-vendor-2fa",
  steps: [
    {
      target: '[data-tour="admin-vendor-2fa-section"]',
      title: "Vendor 2FA Resets",
      body: "When a vendor is locked out because they lost access to their authenticator app, an admin must reset their TOTP secret here so they can re-enroll. Search for the vendor by email or name.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-vendor-2fa-section"]',
      title: "⚠️ Security-sensitive action",
      body: "Only reset 2FA after verifying the vendor's identity through another channel (a call or known email). A reset immediately invalidates their existing TOTP codes — the vendor must re-scan a new QR code on their next login.",
      placement: "top",
    },
  ],
};

export const adminVendorEmailResetGuide: TourConfig = {
  id: "admin-vendor-email-reset",
  steps: [
    {
      target: '[data-tour="admin-vendor-email-reset-section"]',
      title: "Vendor Email Resets",
      body: "If a vendor's email changes (e.g., they switched businesses), use this panel to update their login email. The change takes effect immediately — their old email will no longer work for login.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-vendor-email-reset-section"]',
      title: "Verification first",
      body: "Always verify the request is legitimate before changing a vendor email. An unauthorized email change is equivalent to a full account takeover. Confirm via the host who manages this vendor or a known contact.",
      placement: "top",
    },
  ],
};

export const adminBetaFeaturesGuide: TourConfig = {
  id: "admin-beta-features",
  steps: [
    {
      target: '[data-tour="admin-beta-features-new"]',
      title: "Beta Features",
      body: "Beta feature flags let you enable experimental functionality for specific users or groups before a general release. Click New Feature to create a flag with a name, description, and list of opted-in users.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-beta-features-list"]',
      title: "Adding and removing users",
      body: "Click a flag to expand it and add or remove individual user emails. Users see their beta features in their Profile → Early Access tab. Removing a user from a flag immediately disables the feature for them on next page load.",
      placement: "right",
    },
  ],
};

export const adminMenuTierLocksGuide: TourConfig = {
  id: "admin-menu-tier-locks",
  steps: [
    {
      target: '[data-tour="admin-menu-tier-locks-table"]',
      title: "Menu Tier Locks",
      body: "Tier locks control which navigation items are visible and accessible to each subscription tier. Items marked as locked for a tier show a locked icon in the sidebar and redirect to an upgrade prompt.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-menu-tier-locks-save-all"]',
      title: "Editing locks",
      body: "Toggle any menu item to enable or restrict it for a given tier, then click Save to apply. Changes apply instantly to all users on that tier — no deploy required. Be careful: unlocking a feature for a lower tier may expose functionality that is not yet production-ready.",
      placement: "bottom",
    },
  ],
};

export const adminTosVersionsGuide: TourConfig = {
  id: "admin-tos-versions",
  steps: [
    {
      target: '[data-tour="admin-tos-versions-section"]',
      title: "Terms of Service Versions",
      body: "Each published version of the PadPulse Terms of Service is tracked here. When a new version is published, users who haven't accepted it are prompted on their next login.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-tos-versions-section"]',
      title: "Publishing a new version",
      body: "Click 'New Version' to draft and publish a new ToS. Once published, the version is live. Users must accept the new terms before they can continue using the app. Use ToS Notifications to send an advance heads-up before the new version goes live.",
      placement: "top",
    },
  ],
};

export const adminTosNotificationsGuide: TourConfig = {
  id: "admin-tos-notifications",
  steps: [
    {
      target: '[data-tour="admin-tos-notifications-section"]',
      title: "ToS Notifications",
      body: "Send an advance email notification to all active users before a new Terms of Service goes live. This gives them time to review the changes before their next session triggers the acceptance prompt.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-tos-notifications-section"]',
      title: "One-time send",
      body: "Each notification is a one-time blast to all users with an active subscription. Preview the email content carefully before sending — it cannot be recalled once dispatched.",
      placement: "top",
    },
  ],
};

export const adminPrivacyPolicyNotifyGuide: TourConfig = {
  id: "admin-privacy-policy-notify",
  steps: [
    {
      target: '[data-tour="admin-privacy-policy-notify-section"]',
      title: "Privacy Policy Notify",
      body: "Send an email notifying all active users that the Privacy Policy has been updated. Unlike the ToS, no re-acceptance is required — this is a transparency notification only.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-privacy-policy-notify-section"]',
      title: "Sending",
      body: "Provide the effective date and a short summary of what changed. The email links to the current policy. Notifications are logged so you can see when each blast was sent.",
      placement: "top",
    },
  ],
};

export const adminBillingErrorsGuide: TourConfig = {
  id: "admin-billing-errors",
  steps: [
    {
      target: '[data-tour="admin-billing-errors-section"]',
      title: "Billing Errors",
      body: "Stripe webhook events that fail processing (failed charges, payment method errors, subscription issues) are captured here so no billing event is silently lost.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-billing-errors-section"]',
      title: "Reviewing errors",
      body: "Each entry shows the Stripe event type, the affected user, the error message, and the timestamp. Cross-reference with the Stripe Dashboard for full transaction details. After resolving an issue manually, mark the error as acknowledged so it doesn't stay in the queue.",
      placement: "top",
    },
  ],
};

export const adminPromoCodesGuide: TourConfig = {
  id: "admin-promo-codes",
  steps: [
    {
      target: '[data-tour="admin-promo-codes-new"]',
      title: "Promo Codes",
      body: "Create and manage promotional discount codes that hosts can apply when subscribing. Click New Promo Code — each code can have a fixed or percentage discount, an expiry date, and a usage limit.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-promo-codes-table"]',
      title: "Stripe sync",
      body: "Promo codes created here are synced to Stripe as coupon codes. When a user applies a code at checkout, Stripe validates it and applies the discount to their subscription. Expired or over-limit codes are automatically rejected by Stripe.",
      placement: "top",
    },
  ],
};

export const adminPaymentInstructionsGuide: TourConfig = {
  id: "admin-payment-instructions",
  steps: [
    {
      target: '[data-tour="admin-payment-instructions-section"]',
      title: "Payment Instructions",
      body: "This is the bank transfer and manual payment instructions block shown to founders who pay via wire or check. Edit this content to update routing numbers, account details, or instructions.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-payment-instructions-section"]',
      title: "⚠️ Sensitive financial content",
      body: "This content is rendered directly in the host-facing UI. Always double-check routing numbers and account details before saving. An incorrect account number could result in misrouted payments that are difficult to recover.",
      placement: "top",
    },
  ],
};

export const adminReferralCommissionsGuide: TourConfig = {
  id: "admin-referral-commissions",
  steps: [
    {
      target: '[data-tour="admin-referral-commissions-section"]',
      title: "Referral Commissions",
      body: "Track all partner referral commissions generated from lender, broker, and realtor deals. Each row shows the partner, the deal, the commission rate, the amount earned, and payment status.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-referral-commissions-section"]',
      title: "Marking paid",
      body: "After processing a commission payout (via ACH, check, or Stripe), mark it as 'Paid' here to keep the ledger accurate. This does not trigger any automatic payment — it's a record-keeping action.",
      placement: "top",
    },
  ],
};

export const adminStripeWipeGuide: TourConfig = {
  id: "admin-stripe-wipe",
  steps: [
    {
      target: '[data-tour="admin-stripe-wipe-section"]',
      title: "Wipe Stripe Test Data",
      body: "This panel deletes all Stripe test-mode customers, subscriptions, and payment methods for PadPulse. It is intended for resetting the sandbox environment during development and QA.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-stripe-wipe-section"]',
      title: "⛔ Destructive — test mode only",
      body: "This action is irreversible and affects ALL test-mode Stripe objects. Never run this in production mode. Confirm you are using the Stripe test API key before proceeding. There is no undo — all cleared data is permanently deleted from Stripe.",
      placement: "top",
    },
  ],
};

export const adminRejectedTasksGuide: TourConfig = {
  id: "admin-rejected-tasks",
  steps: [
    {
      target: '[data-tour="admin-rejected-tasks-heading"]',
      title: "Rejected Vendor Tasks",
      body: "When a vendor rejects a task assigned to them, the rejection is logged here. Each entry shows the task, the vendor, the rejection reason, and the timestamp.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-rejected-tasks-table"]',
      title: "Follow-up action",
      body: "A rejected task still needs to be reassigned by the host. Use this log to spot patterns — if a vendor is rejecting many tasks, it may indicate a routing mismatch or a vendor availability issue worth discussing with the host.",
      placement: "top",
    },
  ],
};

export const adminVendorMarketplaceFeesGuide: TourConfig = {
  id: "admin-vendor-marketplace-fees",
  steps: [
    {
      target: '[data-tour="admin-vendor-marketplace-fees-summary"]',
      title: "Vendor Marketplace Fees",
      body: "Track the marketplace listing fees charged to vendors through the PadPulse Marketplace. The summary cards show how many vendors have opted in, how many are listed, and the all-time revenue collected.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-vendor-marketplace-fees-records"]',
      title: "Monthly fee records",
      body: "Each row is a single marketplace charge — one per vendor per calendar month — with the linked task and Stripe payment transaction. Search by vendor or month, and follow the Stripe link for full transaction details.",
      placement: "top",
    },
  ],
};

export const adminPartnersApplicationsGuide: TourConfig = {
  id: "admin-partners-applications",
  steps: [
    {
      target: '[data-tour="admin-partners-applications-section"]',
      title: "Partner Applications",
      body: "When a lender, broker, realtor, or wholesaler submits a partnership application, it appears here. Applications start in 'Pending' status. Filter by type or status to find what needs attention.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-applications-section"]',
      title: "Approve workflow",
      body: "Clicking Approve creates the partner's account, grants portal access, and sends them a welcome email with their login instructions. This is a one-click action — confirm the applicant's identity and details before approving.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-partners-applications-section"]',
      title: "Reject or request more info",
      body: "Use Reject to decline an application with an optional note (sent to the applicant). Use 'Needs More Info' to pause the application and request additional details. Both actions email the applicant automatically.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-partners-applications-section"]',
      title: "Direct invite",
      body: "Use the 'Invite Partner' button to bypass the application form and directly create a partner account. Useful when onboarding a partner you've already vetted off-platform.",
      placement: "top",
    },
  ],
};

export const adminPartnersAgreementsGuide: TourConfig = {
  id: "admin-partners-agreements",
  steps: [
    {
      target: '[data-tour="admin-partners-agreements-section"]',
      title: "Agreement Submissions",
      body: "When a partner signs the PadPulse partnership agreement via DocuSign (or a similar flow), the completed document lands here. Each row shows the partner name, agreement type, signing date, and document status.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-agreements-section"]',
      title: "Verification",
      body: "Review the signed agreement to confirm the correct party signed, the details match the application, and the agreement version is current. Mark as 'Verified' once reviewed. Unverified agreements are flagged until cleared.",
      placement: "top",
    },
  ],
};

export const adminPartnersAllGuide: TourConfig = {
  id: "admin-partners-all",
  steps: [
    {
      target: '[data-tour="admin-partners-all-section"]',
      title: "All Partners",
      body: "A full list of all active partners across every type (lender, broker, realtor, wholesaler). Use this as your master directory for searching, filtering, and managing the complete partner roster.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-all-section"]',
      title: "Partner actions",
      body: "Click any partner row to open their detail view. From there you can see their profile, associated deals, referral commissions, and portal access status. You can deactivate or reinstate a partner from the detail view.",
      placement: "top",
    },
  ],
};

export const adminPartnersRealtorsGuide: TourConfig = {
  id: "admin-partners-realtors",
  steps: [
    {
      target: '[data-tour="admin-partners-realtors-section"]',
      title: "Realtor Partners",
      body: "The realtor partner directory is managed here. Active realtors appear in the Finance → Realtors section that hosts can browse when looking for co-living-experienced agents.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-realtors-section"]',
      title: "Published listings",
      body: "Each realtor can have a published profile card in the directory. Toggle the 'Published' flag to control directory visibility. Unpublished realtors are invisible to hosts but their portal access remains intact.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-partners-realtors-section"]',
      title: "Commission tracking",
      body: "Realtor commissions are tracked separately in Referral Commissions. This panel manages the directory listing only. Commission rates are set on each realtor's profile.",
      placement: "top",
    },
  ],
};

export const adminPartnersWholesalersGuide: TourConfig = {
  id: "admin-partners-wholesalers",
  steps: [
    {
      target: '[data-tour="admin-partners-wholesalers-section"]',
      title: "Wholesaler Partners",
      body: "Wholesalers list off-market deals in the Buy Deals marketplace. This panel lets you review their listings, manage their portal access, and see how many deals they've submitted.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-wholesalers-section"]',
      title: "Deal moderation",
      body: "Wholesaler deal listings can be approved or hidden from the public marketplace from this panel. Listings marked as featured appear at the top of the Buy Deals page for hosts.",
      placement: "top",
    },
  ],
};

export const adminLendersBrokersGuide: TourConfig = {
  id: "admin-lenders-brokers",
  steps: [
    {
      target: '[data-tour="admin-lenders-brokers-section"]',
      title: "Lenders & Brokers",
      body: "Co-living lenders and mortgage brokers who are part of the PadPulse Finance Directory are managed here. They appear in the Finance section for hosts seeking financing.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-lenders-brokers-section"]',
      title: "Directory management",
      body: "Toggle 'Published' to control who appears in the Finance Directory. You can set featured lenders that appear first. Each lender's Connect button routes hosts to their partner portal or external form.",
      placement: "top",
    },
  ],
};

export const adminPartnersMessagesGuide: TourConfig = {
  id: "admin-partners-messages",
  steps: [
    {
      target: '[data-tour="admin-partners-messages-section"]',
      title: "Partner Messages",
      body: "Messages sent between hosts and partners through the PadPulse platform are logged here. This is an oversight view — admins can see all partner-host communications for compliance and support purposes.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-messages-section"]',
      title: "Support use",
      body: "Use this panel when a host or partner reports a communication issue (missing message, wrong recipient, delayed delivery). The log shows delivery status and timestamps for each message.",
      placement: "top",
    },
  ],
};

export const adminPartnersApplicationAlertsGuide: TourConfig = {
  id: "admin-partners-application-alerts",
  steps: [
    {
      target: '[data-tour="admin-partners-application-alerts-section"]',
      title: "Application Alerts",
      body: "Configure which admin email addresses receive a notification when a new partner application is submitted. Add or remove recipients to keep the right team members informed.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-partners-application-alerts-section"]',
      title: "Instant notifications",
      body: "Alert emails are sent the moment an application arrives. If no recipients are configured, applications will sit unnoticed in the Applications panel until manually reviewed. Keep at least one active recipient.",
      placement: "top",
    },
  ],
};

export const adminReferHostReferrersGuide: TourConfig = {
  id: "admin-refer-host-referrers",
  steps: [
    {
      target: '[data-tour="admin-refer-host-referrers-section"]',
      title: "Refer-a-Host: Referrers",
      body: "Every host with an active referral link is tracked here. The table shows their unique referral code, how many new hosts they've referred, and their total accrued commission.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-refer-host-referrers-section"]',
      title: "Referral attribution",
      body: "Attribution is automatic — when a new user signs up via a referral link, the referring host is credited. Commissions accrue monthly based on the referred host's active subscription. Manual attribution (for off-link referrals) is not yet supported.",
      placement: "top",
    },
  ],
};

export const adminReferHostStatementsGuide: TourConfig = {
  id: "admin-refer-host-statements",
  steps: [
    {
      target: '[data-tour="admin-refer-host-statements-section"]',
      title: "Quarterly Statements",
      body: "Referral commission statements are generated quarterly and listed here. Each statement summarizes the referrals credited and the total commission owed for that quarter.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-refer-host-statements-section"]',
      title: "Payout process",
      body: "Statements represent what is owed — they do not trigger automatic payouts. Use Affiliate Payouts to record and process the actual disbursement after reviewing the statement.",
      placement: "top",
    },
  ],
};

export const adminReferHostAccrualsGuide: TourConfig = {
  id: "admin-refer-host-accruals",
  steps: [
    {
      target: '[data-tour="admin-refer-host-accruals-section"]',
      title: "Accruals Log",
      body: "Every individual commission accrual event — one per referred host per billing cycle — is recorded here. This is the raw ledger that feeds into quarterly statements.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-refer-host-accruals-section"]',
      title: "Reconciliation",
      body: "Use this log to verify that accruals match Stripe billing events. Filter by referrer or date range to audit a specific quarter before issuing statements.",
      placement: "top",
    },
  ],
};

export const adminAffiliatePayoutsGuide: TourConfig = {
  id: "admin-affiliate-payouts",
  steps: [
    {
      target: '[data-tour="admin-affiliate-payouts-section"]',
      title: "Affiliate Payouts",
      body: "Record payouts to referral program participants here. Each payout entry links to a quarterly statement and records the amount, payment method, and date disbursed.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-affiliate-payouts-section"]',
      title: "Payout workflow",
      body: "After sending a payout (check, ACH, etc.), create a record here with the amount and date. This keeps the payout history attached to the referrer's profile for future audits.",
      placement: "top",
    },
  ],
};

export const adminExplainerVideosGuide: TourConfig = {
  id: "admin-explainer-videos",
  steps: [
    {
      target: '[data-tour="admin-explainer-videos-section"]',
      title: "Explainer Videos",
      body: "Manage the explainer video library surfaced by the 'Explainer Video' button throughout the host app. Each entry links a page key (e.g., 'properties', 'financials') to a YouTube video URL.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-explainer-videos-section"]',
      title: "Page key mapping",
      body: "The page key must exactly match the key used in the host app's ExplainerVideoButton component. Mismatched keys result in the button not appearing. Refer to EXPLAINER_VIDEO_KEYS in the codebase for the canonical list.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-explainer-videos-section"]',
      title: "Updating videos",
      body: "Paste a YouTube video URL (standard or embed format). The video is shown inside the app in a dialog when the host clicks the Explainer Video button for that page. Replace the URL to swap the video with no code change required.",
      placement: "top",
    },
  ],
};

export const adminSampleBuyDealsGuide: TourConfig = {
  id: "admin-sample-buy-deals",
  steps: [
    {
      target: '[data-tour="admin-sample-buy-deals-section"]',
      title: "Sample Buy Deals",
      body: "Sample deals are shown in the Buy Deals marketplace to demonstrate what a typical listing looks like — useful before real wholesaler listings have been submitted. Manage sample entries here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-sample-buy-deals-section"]',
      title: "Show/hide logic",
      body: "Sample deals are only shown when the live deal count falls below a threshold configured in the app settings. They are automatically hidden once enough real listings are present.",
      placement: "top",
    },
  ],
};

export const adminSaleListingsGuide: TourConfig = {
  id: "admin-sale-listings",
  steps: [
    {
      target: '[data-tour="admin-sale-listings-section"]',
      title: "Sell Listings",
      body: "Properties that hosts have listed for sale through PadPulse's Sell Marketplace appear here for moderation. Review listings before they go live to the investor marketplace.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-sale-listings-section"]',
      title: "Listing approval",
      body: "Approve a listing to make it publicly visible on the Sell Marketplace. Reject or hide a listing if it contains incorrect information or doesn't meet quality standards. Hosts are notified when their listing status changes.",
      placement: "top",
    },
  ],
};

export const adminCityAiAnalysesGuide: TourConfig = {
  id: "admin-city-ai-analyses",
  steps: [
    {
      target: '[data-tour="admin-city-ai-analyses-section"]',
      title: "City AI Analyses",
      body: "Platform-wide city analyses generated by Gemini AI are managed here. These analyses power the Market Research section for all hosts and are shared across the platform (not per-host).",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-city-ai-analyses-section"]',
      title: "Regeneration",
      body: "Trigger a fresh Gemini analysis for any city to update stale market data. Generation uses API quota — avoid regenerating dozens of cities at once. Check the Gemini Usage panel to monitor quota consumption.",
      placement: "top",
    },
  ],
};

export const adminMarketResearchPdfGuide: TourConfig = {
  id: "admin-market-research-pdf",
  steps: [
    {
      target: '[data-tour="admin-market-research-pdf-section"]',
      title: "Market PDF Import",
      body: "Upload market research PDFs (e.g., third-party co-living market reports) that are surfaced in the Market Research section for hosts. Uploaded PDFs are attached to specific cities.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-market-research-pdf-section"]',
      title: "File management",
      body: "Each PDF is linked to a city. Hosts in that city see the PDF as a downloadable resource. Remove or replace PDFs when newer reports are available. File size limits apply — check the upload dialog for the current limit.",
      placement: "top",
    },
  ],
};

export const adminGeminiUsageGuide: TourConfig = {
  id: "admin-gemini-usage",
  steps: [
    {
      target: '[data-tour="admin-gemini-usage-section"]',
      title: "Gemini API Usage",
      body: "Monitor Gemini API usage across the platform. This panel shows total requests, token consumption, and a per-user breakdown of who is generating the most AI content.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-gemini-usage-section"]',
      title: "Quota awareness",
      body: "Gemini has daily and monthly quota limits. If usage spikes (e.g., a host generates hundreds of listings), you may approach quota limits. Set alerts in the Gemini Alert Settings card to get notified before hitting the ceiling.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-gemini-usage-section"]',
      title: "Per-user drilldown",
      body: "Click a user row to see a detailed breakdown of their Gemini requests by feature (listing descriptions, market analysis, etc.). Useful for identifying unusual usage patterns.",
      placement: "top",
    },
  ],
};

export const adminApifyUsageGuide: TourConfig = {
  id: "admin-apify-usage",
  steps: [
    {
      target: '[data-tour="admin-apify-usage-section"]',
      title: "Apify Usage",
      body: "Track Apify actor runs used for Zillow property scraping (Buy Box city scans and single-listing imports). Each run consumes Apify compute units — monitor here to avoid unexpected bill spikes.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-apify-usage-section"]',
      title: "Actor status",
      body: "Runs show as succeeded, failed, or timed out. Failed runs typically indicate a bad URL format or a quota issue. Check the Gotchas in the project docs — Buy Box city scans require a specific Zillow URL format with searchQueryState.",
      placement: "top",
    },
  ],
};

export const adminElevenlabsUsageGuide: TourConfig = {
  id: "admin-elevenlabs-usage",
  steps: [
    {
      target: '[data-tour="admin-elevenlabs-usage-section"]',
      title: "ElevenLabs Voice Usage",
      body: "Monitor ElevenLabs text-to-speech API usage for the Pulse Digest voice briefing feature. Each briefing generation consumes character quota. This panel shows total usage and a daily breakdown.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-elevenlabs-usage-section"]',
      title: "Quota management",
      body: "ElevenLabs quotas reset monthly. If a large number of hosts request briefings on the same day, usage can spike quickly. The Voice Briefing Cache panel shows cached audio that avoids re-generating identical briefings.",
      placement: "top",
    },
  ],
};

export const adminVoiceAudioCacheGuide: TourConfig = {
  id: "admin-voice-audio-cache",
  steps: [
    {
      target: '[data-tour="admin-voice-audio-cache-section"]',
      title: "Voice Briefing Cache",
      body: "Cached voice briefing audio files are listed here. When two hosts would receive identical briefing content, PadPulse serves the cached audio instead of regenerating it — saving ElevenLabs quota.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-voice-audio-cache-section"]',
      title: "Cache management",
      body: "Clear individual cache entries to force regeneration of a briefing (e.g., after updating the briefing template). Clearing the cache does not affect hosts — they just trigger a fresh generation on their next briefing request.",
      placement: "top",
    },
  ],
};

export const adminPulseDigestGuide: TourConfig = {
  id: "admin-pulse-digest",
  steps: [
    {
      target: '[data-tour="admin-pulse-digest-section"]',
      title: "Pulse Digest Health",
      body: "The Pulse Digest is the weekly AI-generated performance briefing sent to hosts. This panel shows delivery health — how many digests were generated, sent, failed, and skipped in each send window.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-pulse-digest-section"]',
      title: "Failure triage",
      body: "Failed digests are flagged here with the error reason. Common causes: missing property data, Gemini quota exhaustion, or an email delivery bounce. Retry failed digests manually after resolving the underlying issue.",
      placement: "top",
    },
  ],
};

export const adminPulseFeedbackGuide: TourConfig = {
  id: "admin-pulse-feedback",
  steps: [
    {
      target: '[data-tour="admin-pulse-feedback-section"]',
      title: "Pulse Feedback",
      body: "After receiving a Pulse Digest, hosts can rate it (thumbs up/down) and leave a comment. This panel aggregates all feedback so you can assess briefing quality and identify improvement areas.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-pulse-feedback-section"]',
      title: "Acting on feedback",
      body: "Filter by rating to read negative feedback first. Patterns in the comments indicate what the briefing is missing or getting wrong. Use this to guide improvements to the Gemini prompt or the briefing structure.",
      placement: "top",
    },
  ],
};

export const adminAiCategorySuggestionsGuide: TourConfig = {
  id: "admin-ai-category-suggestions",
  steps: [
    {
      target: '[data-tour="admin-ai-category-suggestions-section"]',
      title: "AI Category Suggestions",
      body: "When Gemini suggests a new expense category that doesn't match an existing one, the suggestion is queued here for admin review. Approve a suggestion to add it to the platform-wide category list.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-ai-category-suggestions-section"]',
      title: "Approval workflow",
      body: "Review the suggested category name and the context (which expense triggered it). Approve to add it globally, or dismiss if it's a duplicate or too niche. Approved categories appear for all hosts immediately.",
      placement: "top",
    },
  ],
};

export const adminThermostatPollHealthGuide: TourConfig = {
  id: "admin-thermostat-poll-health",
  steps: [
    {
      target: '[data-tour="admin-thermostat-poll-health-section"]',
      title: "Thermostat Polling Health",
      body: "PadPulse polls Seam for thermostat status on a schedule. This panel shows the health of those polling jobs — how many thermostats were polled, how many returned errors, and when the last successful poll ran.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-thermostat-poll-health-section"]',
      title: "Error patterns",
      body: "Frequent polling errors for a specific thermostat often mean the device is offline or the Seam connection has expired. Escalate to the host to reconnect their Seam account or check the device physically.",
      placement: "top",
    },
  ],
};

export const adminMessagesPendingReconcileGuide: TourConfig = {
  id: "admin-messages-pending-reconcile",
  steps: [
    {
      target: '[data-tour="admin-messages-pending-reconcile-section"]',
      title: "Messages Pending Reconcile",
      body: "Outbound PadSplit messages that have been sent but not yet confirmed as delivered are tracked here. A message stays in this queue until the PadSplit harvest job confirms its status.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-messages-pending-reconcile-section"]',
      title: "Stuck messages",
      body: "If a message stays pending for more than an hour, it may be orphaned. The system has an auto-retry mechanism, but persistent failures should be investigated. Check the PadSplit API status and the host's PadSplit credentials.",
      placement: "top",
    },
  ],
};

export const adminAuditLogHealthGuide: TourConfig = {
  id: "admin-audit-log-health",
  steps: [
    {
      target: '[data-tour="admin-audit-log-health-section"]',
      title: "Audit Log Health",
      body: "The audit log records every significant action taken in the platform — user changes, subscription overrides, impersonation sessions, and more. This panel shows the health and size of that log.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-audit-log-health-section"]',
      title: "Abnormal growth alerts",
      body: "If the audit log is growing unusually fast (e.g., a runaway cron or a bug creating duplicate entries), an alert banner appears at the top of the admin panel. Investigate here to identify the source of the spike.",
      placement: "top",
    },
  ],
};

export const adminMessageAttachmentsGuide: TourConfig = {
  id: "admin-message-attachments",
  steps: [
    {
      target: '[data-tour="admin-message-attachments-section"]',
      title: "Message Attachment Storage",
      body: "Files attached to host-member messages are stored in object storage. This panel shows storage usage statistics — total file count, aggregate size, and a breakdown by host.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-message-attachments-section"]',
      title: "Storage hygiene",
      body: "Attachments from resolved conversations are not automatically purged. If storage costs become a concern, identify and remove large orphaned files from this panel. Direct deletion is permanent and cannot be undone.",
      placement: "top",
    },
  ],
};

export const adminChangelogGuide: TourConfig = {
  id: "admin-changelog",
  steps: [
    {
      target: '[data-tour="admin-changelog-section"]',
      title: "Change Log",
      body: "Every released task is logged here as a changelog entry — title, description, date, and the release SHA. Hosts see a simplified version of this in their in-app 'What's New' feed.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-changelog-section"]',
      title: "Managing entries",
      body: "Changelog entries are auto-generated from the task history sync. You can edit descriptions, toggle visibility (hide experimental entries from the host-facing feed), or pin important releases to the top.",
      placement: "top",
    },
  ],
};

export const adminSecretScanGuide: TourConfig = {
  id: "admin-secret-scan",
  steps: [
    {
      target: '[data-tour="admin-secret-scan-section"]',
      title: "Secret Scan",
      body: "Run a static scan of the codebase for accidentally committed secrets — API keys, tokens, credentials, and other sensitive strings. This scan runs against the current deployed code.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-secret-scan-section"]',
      title: "Scan results",
      body: "Each finding shows the file path, line number, secret type, and a masked preview. False positives can be acknowledged and excluded from future scan reports. Real findings must be rotated immediately — revoke the exposed credential before closing the finding.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-secret-scan-section"]',
      title: "⚠️ Rotate before acknowledging",
      body: "If a scan finds a real secret, your first action must be to revoke or rotate that credential in the provider dashboard (Stripe, Twilio, etc.) before marking it acknowledged here. An acknowledged-but-unrotated secret is still compromised.",
      placement: "top",
    },
  ],
};

export const adminEncryptionAuditGuide: TourConfig = {
  id: "admin-encryption-audit",
  steps: [
    {
      target: '[data-tour="admin-encryption-audit-section"]',
      title: "Encryption Audit",
      body: "Review the encryption status of sensitive fields stored in the database. This panel identifies fields that contain sensitive data and whether they are encrypted at rest.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-encryption-audit-section"]',
      title: "Audit results",
      body: "Fields flagged as 'unencrypted' should be reviewed for sensitivity. If a sensitive field is unencrypted, schedule a migration to add encryption. This panel is read-only — encryption changes require a code deploy.",
      placement: "top",
    },
  ],
};

export const adminImportantLinksGuide: TourConfig = {
  id: "admin-important-links",
  steps: [
    {
      target: '[data-tour="admin-important-links-section"]',
      title: "Important Links",
      body: "Manage the curated list of links shown to admin staff — internal dashboards, Stripe, Twilio, Sentry, external docs, and other key resources. These are admin-facing only.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-important-links-section"]',
      title: "Adding links",
      body: "Add new links with a label, URL, and category. Links appear grouped by category in the admin sidebar's quick-access area. Keep this list current — stale links cause confusion when staff go to the wrong dashboard.",
      placement: "top",
    },
  ],
};

export const adminConferencePromoGuide: TourConfig = {
  id: "admin-conference-promo",
  steps: [
    {
      target: '[data-tour="admin-conference-promo-section"]',
      title: "Conference Promo Banner",
      body: "Control the promotional banner shown at the top of the marketing site for conference events. Toggle it on or off, update the headline, CTA, and link without a code deploy.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-conference-promo-section"]',
      title: "Live preview",
      body: "Changes take effect on the next page load for site visitors. The banner is shown sitewide when enabled. Disable it promptly after the event window closes to avoid showing stale promotional content.",
      placement: "top",
    },
  ],
};

export const adminFoundingAccessDateGuide: TourConfig = {
  id: "admin-founding-access-date",
  steps: [
    {
      target: '[data-tour="admin-founding-access-date-section"]',
      title: "Founder Circle Launch Date",
      body: "Set the date when Founder Circle access opens. This date controls the countdown timer shown to founders who have reserved a spot but whose tier hasn't opened yet.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-founding-access-date-input"]',
      title: "Set the launch date",
      body: "Pick a date and time using your browser's local timezone. Leave the field blank to fall back to the environment variable default. The preview below the input shows the effective date that will be used.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-founding-access-date-save"]',
      title: "⚠️ Public-facing — save carefully",
      body: "This date is shown directly to founders via the marketing site and any founder-specific emails. If you change this date, founders will see the updated countdown immediately. Coordinate with the marketing team before saving.",
      placement: "top",
    },
  ],
};

export const adminDashboardLayoutsGuide: TourConfig = {
  id: "admin-dashboard-layouts",
  steps: [
    {
      target: '[data-tour="admin-dashboard-layouts-section"]',
      title: "Dashboard Tier Defaults",
      body: "Set the default panel order and visibility for each subscription tier. Any host who hasn't customized their own dashboard inherits exactly the layout you configure here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-dashboard-layouts-section"]',
      title: "Selecting a tier",
      body: "Pick a tier from the list on the left to load its current panel configuration. Changes are per-tier and independent — updating one tier does not affect others.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-dashboard-layouts-section"]',
      title: "Reordering and hiding panels",
      body: "Drag panels into the order you want and toggle the eye icon to hide panels from the default layout. A checkmark appears on the tier row once its settings have been saved.",
      placement: "top",
    },
  ],
};

export const adminRecommendedFurnishingsGuide: TourConfig = {
  id: "admin-recommended-furnishings",
  steps: [
    {
      target: '[data-tour="admin-recommended-furnishings-section"]',
      title: "Recommended Furnishings",
      body: "Manage the curated product list shown on the public /recommendedfurnishings page. Items here are organized by room type and include affiliate links tracked for clicks.",
      placement: "bottom",
    },
    {
      target: '[data-tour="admin-recommended-furnishings-section"]',
      title: "Bulk update via XLSX",
      body: "Export the current list as an XLSX file, make your edits in a spreadsheet, then import it back. The import will validate each row and report any errors before committing.",
      placement: "top",
    },
    {
      target: '[data-tour="admin-recommended-furnishings-section"]',
      title: "Click analytics",
      body: "The analytics panel shows how many times each item's affiliate link was clicked over time. Use this data to prioritize high-interest products and retire items with low engagement.",
      placement: "top",
    },
  ],
};

export const ALL_ADMIN_GUIDES: TourConfig[] = [
  adminLeadsGuide,
  adminConferenceLeadsGuide,
  adminFeaturesGuide,
  adminFounderIntentsGuide,
  adminAppUsersGuide,
  adminImpersonationLogGuide,
  adminVendor2faGuide,
  adminVendorEmailResetGuide,
  adminBetaFeaturesGuide,
  adminMenuTierLocksGuide,
  adminTosVersionsGuide,
  adminTosNotificationsGuide,
  adminPrivacyPolicyNotifyGuide,
  adminBillingErrorsGuide,
  adminPromoCodesGuide,
  adminPaymentInstructionsGuide,
  adminReferralCommissionsGuide,
  adminStripeWipeGuide,
  adminRejectedTasksGuide,
  adminVendorMarketplaceFeesGuide,
  adminPartnersApplicationsGuide,
  adminPartnersAgreementsGuide,
  adminPartnersAllGuide,
  adminPartnersRealtorsGuide,
  adminPartnersWholesalersGuide,
  adminLendersBrokersGuide,
  adminPartnersMessagesGuide,
  adminPartnersApplicationAlertsGuide,
  adminReferHostReferrersGuide,
  adminReferHostStatementsGuide,
  adminReferHostAccrualsGuide,
  adminAffiliatePayoutsGuide,
  adminExplainerVideosGuide,
  adminSampleBuyDealsGuide,
  adminSaleListingsGuide,
  adminCityAiAnalysesGuide,
  adminMarketResearchPdfGuide,
  adminGeminiUsageGuide,
  adminApifyUsageGuide,
  adminElevenlabsUsageGuide,
  adminVoiceAudioCacheGuide,
  adminPulseDigestGuide,
  adminPulseFeedbackGuide,
  adminAiCategorySuggestionsGuide,
  adminThermostatPollHealthGuide,
  adminMessagesPendingReconcileGuide,
  adminAuditLogHealthGuide,
  adminMessageAttachmentsGuide,
  adminChangelogGuide,
  adminSecretScanGuide,
  adminEncryptionAuditGuide,
  adminImportantLinksGuide,
  adminConferencePromoGuide,
  adminFoundingAccessDateGuide,
  adminDashboardLayoutsGuide,
  adminRecommendedFurnishingsGuide,
];
